import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Select,
  Button,
  Space,
  Typography,
  Row,
  Col,
  message,
  Spin,
  Image,
  Checkbox,
  Modal,
  Input,
  Form,
  Table,
  Tag,
  Divider,
  Empty,
  App
} from 'antd';
import {
  UserOutlined,
  InstagramOutlined,
  UploadOutlined,
  CheckOutlined,
  EditOutlined,
  ReloadOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, AdminReportsUser } from '../lib/api';
import {
  getInstagramPosts,
  adminBatchProcessImages,
  getPendingItems,
  getPendingSources,
  deletePendingSources,
  updatePendingItem,
  confirmPendingItems,
  PendingItem,
  PendingSource,
  ConfirmItemPayload,
  InstagramPost,
  InstagramPostsResponse
} from '../lib/api';
import { DetectedItemCard } from '../components/DetectedItemCard';
import { EditTagsModal } from '../components/EditTagsModal';
import '../components/DetectedItemCard.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const InstagramBatchProcess: React.FC = () => {
  const queryClient = useQueryClient();
  const { modal } = App.useApp();
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [instagramUsername, setInstagramUsername] = useState<string>('');
  const [debouncedInstagramUsername, setDebouncedInstagramUsername] = useState<string>('');
  const [selectedPosts, setSelectedPosts] = useState<Set<string>>(new Set()); // For backward compatibility, but we'll use selectedImages
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set()); // Store image URLs
  const [selectedPendingIds, setSelectedPendingIds] = useState<Set<string>>(new Set());
  const [editingItem, setEditingItem] = useState<PendingItem | null>(null);
  const [isEditingModalVisible, setIsEditingModalVisible] = useState(false);
  const [editingForm] = Form.useForm();
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [selectedSourceIndex, setSelectedSourceIndex] = useState<number | null>(null);
  const [sourceImageUrls, setSourceImageUrls] = useState<Record<string, string>>({});
  const [isCreateUserModalVisible, setIsCreateUserModalVisible] = useState(false);
  const [createUserForm] = Form.useForm();
  
  // Lazy loading state
  const [allPosts, setAllPosts] = useState<InstagramPost[]>([]);
  const [postsOffset, setPostsOffset] = useState<number>(0);
  const postsOffsetRef = React.useRef<number>(0); // Ref to track current offset for useCallback
  const [nextMaxId, setNextMaxId] = useState<string | undefined>(undefined); // Track pagination cursor
  const nextMaxIdRef = React.useRef<string | undefined>(undefined); // Ref to track current max_id for useCallback
  const [isLoadingMorePosts, setIsLoadingMorePosts] = useState<boolean>(false);
  const [hasMorePosts, setHasMorePosts] = useState<boolean>(true);
  const POSTS_PER_PAGE = 12; // Load 12 posts per batch

  // Debounce Instagram username to avoid too many API calls while typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedInstagramUsername(instagramUsername);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [instagramUsername]);
  
  // Reset posts when username changes
  useEffect(() => {
    if (debouncedInstagramUsername) {
      setAllPosts([]);
      setPostsOffset(0);
      postsOffsetRef.current = 0; // Reset ref as well
      setNextMaxId(undefined);
      nextMaxIdRef.current = undefined; // Reset max_id ref as well
      setHasMorePosts(true);
    }
  }, [debouncedInstagramUsername]);

  // Fetch users for selection
  const { data: usersData, refetch: refetchUsers } = useQuery({
    queryKey: ['users'],
    queryFn: () => adminApi.getUsers({ limit: 1000 }),
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: (userData: { username: string; password: string; email?: string; full_name?: string; telegram_id?: string }) =>
      adminApi.createUser(userData),
    onSuccess: (data) => {
      if (data.success && data.user) {
        message.success(`User "${data.user.username}" created successfully!`);
        setIsCreateUserModalVisible(false);
        createUserForm.resetFields();
        // Refresh users list
        refetchUsers();
        // Select the newly created user
        setSelectedUserId(data.user.user_id);
      } else {
        message.error(data.error || 'Failed to create user');
      }
    },
    onError: (error: any) => {
      const errorMsg = error?.response?.data?.error || error?.message || 'Failed to create user';
      message.error(errorMsg);
    },
  });

  // Fetch Instagram posts (using debounced username) - initial load of 12 posts
  const {
    data: instagramData,
    isLoading: loadingPosts,
    refetch: refetchPosts,
    error: postsError
  } = useQuery<InstagramPostsResponse>({
    queryKey: ['instagram-posts', debouncedInstagramUsername, 0],
    queryFn: () => getInstagramPosts(debouncedInstagramUsername, POSTS_PER_PAGE, 0),
    enabled: !!debouncedInstagramUsername && debouncedInstagramUsername.length > 0,
    retry: false, // Don't retry automatically for rate limits
  });
  
  // Update accumulated posts when initial data loads
  useEffect(() => {
    if (instagramData?.posts) {
      console.log('[Initial Load] Received data:', {
        postsCount: instagramData.posts.length,
        next_max_id: instagramData.next_max_id,
        hasNextMaxId: !!instagramData.next_max_id
      });
      setAllPosts(instagramData.posts);
      const newOffset = instagramData.posts.length;
      setPostsOffset(newOffset);
      postsOffsetRef.current = newOffset; // Update ref as well
      setNextMaxId(instagramData.next_max_id);
      nextMaxIdRef.current = instagramData.next_max_id; // Update max_id ref
      // If we got fewer posts than requested or no next_max_id, there are no more posts
      const hasMore = instagramData.posts.length >= POSTS_PER_PAGE && !!instagramData.next_max_id;
      console.log('[Initial Load] Setting hasMorePosts:', hasMore);
      setHasMorePosts(hasMore);
    }
  }, [instagramData]);
  
  // Load more posts function - wrapped in useCallback to prevent stale closures
  const loadMorePosts = useCallback(async () => {
    if (isLoadingMorePosts || !hasMorePosts || !debouncedInstagramUsername) {
      return;
    }
    
    setIsLoadingMorePosts(true);
    try {
      const currentMaxId = nextMaxIdRef.current; // Use ref for current max_id
      const currentOffset = postsOffsetRef.current; // Use ref for current offset
      
      // If we have max_id, use cursor-based pagination (more efficient)
      // When using cursor pagination, offset should be 0 (or not sent) since cursor handles pagination
      if (currentMaxId) {
        console.log(`[Lazy Load] Using cursor pagination: max_id=${currentMaxId}, limit=${POSTS_PER_PAGE}`);
        // When using max_id, offset is ignored by backend, but we pass 0 to be explicit
        const response = await getInstagramPosts(debouncedInstagramUsername, POSTS_PER_PAGE, 0, currentMaxId);
        
        console.log('[Lazy Load] Received posts:', response.posts?.length || 0);
        if (response.posts && response.posts.length > 0) {
          setAllPosts(prev => {
            const updated = [...prev, ...response.posts];
            console.log('[Lazy Load] Total posts after update:', updated.length);
            return updated;
          });
          const newOffset = currentOffset + response.posts.length;
          setPostsOffset(newOffset);
          postsOffsetRef.current = newOffset; // Update ref
          setNextMaxId(response.next_max_id);
          nextMaxIdRef.current = response.next_max_id; // Update max_id ref
          setHasMorePosts(response.posts.length >= POSTS_PER_PAGE && !!response.next_max_id);
        } else {
          console.log('[Lazy Load] No more posts available');
          setHasMorePosts(false);
        }
      } else {
        // Fallback to offset-based pagination if no max_id
        console.log(`[Lazy Load] Using offset pagination: offset=${currentOffset}, limit=${POSTS_PER_PAGE}`);
        const response = await getInstagramPosts(debouncedInstagramUsername, POSTS_PER_PAGE, currentOffset);
        
        console.log('[Lazy Load] Received posts:', response.posts?.length || 0);
        if (response.posts && response.posts.length > 0) {
          setAllPosts(prev => {
            const updated = [...prev, ...response.posts];
            console.log('[Lazy Load] Total posts after update:', updated.length);
            return updated;
          });
          const newOffset = currentOffset + response.posts.length;
          setPostsOffset(newOffset);
          postsOffsetRef.current = newOffset; // Update ref
          setNextMaxId(response.next_max_id);
          nextMaxIdRef.current = response.next_max_id; // Update max_id ref for next time
          setHasMorePosts(response.posts.length >= POSTS_PER_PAGE && !!response.next_max_id);
        } else {
          console.log('[Lazy Load] No more posts available');
          setHasMorePosts(false);
        }
      }
    } catch (error) {
      console.error('Error loading more posts:', error);
      message.error('Failed to load more posts');
    } finally {
      setIsLoadingMorePosts(false);
    }
  }, [isLoadingMorePosts, hasMorePosts, debouncedInstagramUsername]);


  // Fetch pending sources for selected user (poll every 10 seconds)
  const {
    data: pendingSources,
    isLoading: loadingSources,
    refetch: refetchSources
  } = useQuery({
    queryKey: ['pending-sources', selectedUserId],
    queryFn: () => getPendingSources(selectedUserId),
    enabled: !!selectedUserId,
    refetchInterval: 10000, // Refetch every 10 seconds
  });

  // Use sources from API (we no longer derive from items since we only fetch items for selected source)
  const effectiveSources: PendingSource[] = pendingSources || [];

  // Get selected source's image_id
  const selectedSourceImageId = useMemo(() => {
    if (selectedSourceIndex === null || !effectiveSources) return undefined;
    const source = effectiveSources.find(s => s.source_index === selectedSourceIndex);
    return source?.source_image_id;
  }, [selectedSourceIndex, effectiveSources]);

  // Fetch pending items only for selected source (no continuous polling)
  const {
    data: pendingItems,
    isLoading: loadingPending,
    refetch: refetchPending
  } = useQuery({
    queryKey: ['pending-items', selectedUserId, selectedSourceImageId],
    queryFn: () => getPendingItems(selectedUserId, 200, selectedSourceImageId),
    enabled: !!selectedUserId && !!selectedSourceImageId,
    // Removed refetchInterval - no continuous polling
  });

  // Resolve source image URLs
  useEffect(() => {
    if (!effectiveSources || effectiveSources.length === 0 || !selectedUserId) {
      console.log('[Source Images] Skipping URL resolution:', { 
        effectiveSources: effectiveSources?.length || 0, 
        selectedUserId 
      });
      return;
    }
    
    const resolveUrls = async () => {
      const urls: Record<string, string> = {};
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const adminKey = localStorage.getItem('admin_key');
      
      console.log('[Source Images] Resolving URLs for', effectiveSources.length, 'sources');
      
      for (const source of effectiveSources) {
        const params = new URLSearchParams();
        if (adminKey) {
          params.append('x_admin_key', adminKey);
        }
        // Include user_id from source to help backend locate the image
        if (source.user_id) {
          params.append('user_id', source.user_id);
        }
        const queryString = params.toString();
        const imageUrl = queryString
          ? `${apiBase}/admin/images/${source.source_image_id}?${queryString}`
          : `${apiBase}/admin/images/${source.source_image_id}`;
        urls[source.source_image_id] = imageUrl;
        console.log('[Source Images] URL for', source.source_image_id, ':', imageUrl.substring(0, 150));
      }
      
      setSourceImageUrls(urls);
      
      // Auto-select first source if none selected
      if (selectedSourceIndex === null && effectiveSources.length > 0) {
        console.log('[Source Images] Auto-selecting first source:', effectiveSources[0].source_index);
        setSelectedSourceIndex(effectiveSources[0].source_index);
      }
    };
    
    resolveUrls();
  }, [effectiveSources, selectedUserId]);

  // Group pending items by source
  const itemsBySource = useMemo(() => {
    if (!pendingItems) return {};
    const grouped: Record<string, PendingItem[]> = {};
    
    for (const item of pendingItems) {
      const sourceId = item.source_image_id || 'none';
      if (!grouped[sourceId]) {
        grouped[sourceId] = [];
      }
      grouped[sourceId].push(item);
    }
    
    return grouped;
  }, [pendingItems]);

  // Get current source's items
  const currentSourceItems = useMemo(() => {
    if (selectedSourceIndex === null || !effectiveSources) return [];
    const source = effectiveSources.find(s => s.source_index === selectedSourceIndex);
    if (!source) return [];
    return itemsBySource[source.source_image_id] || [];
  }, [selectedSourceIndex, effectiveSources, itemsBySource]);

  // Auto-select all items when currentSourceItems changes
  useEffect(() => {
    if (currentSourceItems.length > 0) {
      const allItemIds = new Set(currentSourceItems.map(item => item.id));
      setSelectedPendingIds(allItemIds);
    }
  }, [currentSourceItems]);

  // Batch process mutation
  const batchProcessMutation = useMutation({
    mutationFn: (imageUrls: string[]) =>
      adminBatchProcessImages(selectedUserId, imageUrls),
    onSuccess: () => {
      message.success('Batch processing started! Items will appear in pending list shortly.');
      setSelectedImages(new Set());
      setSelectedPosts(new Set());
      setTimeout(() => {
        refetchPending();
      }, 2000);
    },
    onError: (error: any) => {
      message.error(`Failed to start batch process: ${error.message || 'Unknown error'}`);
    },
  });

  // Update pending item mutation
  const updateItemMutation = useMutation({
    mutationFn: ({ userId, itemId, data }: { userId: string; itemId: string; data: any }) =>
      updatePendingItem(userId, itemId, data),
    onSuccess: () => {
      message.success('Item updated successfully');
      setIsEditingModalVisible(false);
      setEditingItem(null);
      refetchPending();
    },
    onError: (error: any) => {
      message.error(`Failed to update item: ${error.message || 'Unknown error'}`);
    },
  });

  // Confirm items mutation
  const confirmItemsMutation = useMutation({
    mutationFn: (items: ConfirmItemPayload[]) => {
      if (!selectedUserId) {
        throw new Error('User ID is required');
      }
      console.log('[Mutation] Confirming items:', { userId: selectedUserId, itemsCount: items.length });
      return confirmPendingItems(selectedUserId, items);
    },
    onSuccess: (data) => {
      console.log('[Mutation] Success:', data);
      message.success(`Successfully added ${data.count} items to closet`);
      setSelectedPendingIds(new Set());
      refetchPending();
    },
    onError: (error: any) => {
      console.error('[Mutation] Error:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      message.error(`Failed to confirm items: ${errorMessage}`);
    },
  });

  // Delete sources mutation
  const deleteSourcesMutation = useMutation({
    mutationFn: (sourceImageIds: string[]) => {
      if (!selectedUserId) {
        throw new Error('User ID is required');
      }
      return deletePendingSources(selectedUserId, sourceImageIds);
    },
    onSuccess: (data) => {
      message.success(`Successfully deleted ${data.deleted} source${data.deleted !== 1 ? 's' : ''}`);
      refetchSources();
      // Clear selection if deleted source was selected
      setSelectedSourceIndex(null);
      refetchPending();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error';
      message.error(`Failed to delete sources: ${errorMessage}`);
    },
  });

  const handleDeleteSource = (e: React.MouseEvent, sourceImageId: string) => {
    e.stopPropagation(); // Prevent triggering source selection
    modal.confirm({
      title: 'Delete Source',
      content: 'Are you sure you want to delete this source image and all its detected items?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        deleteSourcesMutation.mutate([sourceImageId]);
      },
    });
  };

  const handleDeleteAllSources = () => {
    if (!effectiveSources || effectiveSources.length === 0) {
      message.warning('No sources to delete');
      return;
    }
    
    modal.confirm({
      title: 'Delete All Sources',
      content: `Are you sure you want to delete all ${effectiveSources.length} source images and all their detected items? This action cannot be undone.`,
      okText: 'Delete All',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        const allSourceImageIds = effectiveSources.map(s => s.source_image_id);
        deleteSourcesMutation.mutate(allSourceImageIds);
      },
    });
  };

  const handleSelectPost = (postId: string) => {
    const newSelected = new Set(selectedPosts);
    if (newSelected.has(postId)) {
      newSelected.delete(postId);
    } else {
      newSelected.add(postId);
    }
    setSelectedPosts(newSelected);
  };

  const handleSelectImage = (imageUrl: string) => {
    const newSelected = new Set(selectedImages);
    if (newSelected.has(imageUrl)) {
      newSelected.delete(imageUrl);
    } else {
      newSelected.add(imageUrl);
    }
    setSelectedImages(newSelected);
  };

  const handleSelectAllImages = () => {
    if (!allPosts || allPosts.length === 0) return;
    
    // Collect all image URLs from all posts (including lazy loaded ones)
    const allImageUrls: string[] = [];
    allPosts.forEach((post: InstagramPost) => {
      if (post.image_urls && post.image_urls.length > 0) {
        allImageUrls.push(...post.image_urls);
      } else if (post.image_url) {
        allImageUrls.push(post.image_url);
      }
    });

    // Check if all images are selected
    const allSelected = allImageUrls.every(url => selectedImages.has(url));

    if (allSelected) {
      setSelectedImages(new Set());
    } else {
      setSelectedImages(new Set(allImageUrls));
    }
  };
  
  // Intersection Observer for lazy loading
  const loadMoreRef = React.useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const currentRef = loadMoreRef.current;
    console.log('[Intersection Observer] Setting up observer:', {
      hasRef: !!currentRef,
      hasMorePosts,
      isLoadingMorePosts,
      allPostsLength: allPosts.length
    });
    
    if (!currentRef || !hasMorePosts || isLoadingMorePosts) {
      return;
    }
    
    const observer = new IntersectionObserver(
      (entries) => {
        console.log('[Intersection Observer] Entry intersecting:', entries[0].isIntersecting);
        if (entries[0].isIntersecting && hasMorePosts && !isLoadingMorePosts) {
          console.log('[Intersection Observer] Triggering loadMorePosts');
          loadMorePosts();
        }
      },
      { threshold: 0.1 }
    );
    
    observer.observe(currentRef);
    
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMorePosts, isLoadingMorePosts, loadMorePosts, allPosts.length]);

  const handleBatchProcess = () => {
    if (selectedImages.size === 0) {
      message.warning('Please select at least one image to process');
      return;
    }

    // Use directly selected image URLs
    const selectedImageUrls = Array.from(selectedImages);

    if (selectedImageUrls.length === 0) {
      message.warning('No images selected');
      return;
    }

    batchProcessMutation.mutate(selectedImageUrls);
  };

  const handleEditItem = (item: PendingItem) => {
    setEditingItem(item);
    editingForm.setFieldsValue({
      name: item.meta_data?.name || '',
      category: item.meta_data?.category || '',
      color: item.meta_data?.color || '',
      size: item.meta_data?.size || '',
      brand: item.meta_data?.brand || '',
      material: item.meta_data?.material || '',
      season: item.meta_data?.season || '',
      style: item.meta_data?.style || '',
      pattern: item.meta_data?.pattern || '',
      occasion: item.meta_data?.occasion || '',
      notes: item.meta_data?.notes || '',
    });
    setIsEditingModalVisible(true);
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;

    try {
      const values = await editingForm.validateFields();
      await updateItemMutation.mutateAsync({
        userId: selectedUserId,
        itemId: editingItem.id,
        data: {
          meta_data: {
            ...editingItem.meta_data,
            ...values,
          },
        },
      });
    } catch (error) {
      // Form validation error
    }
  };

  const handleConfirmItems = (selectedRowKeys: Set<string> | React.Key[]) => {
    console.log('[Confirm Items] Function called with:', {
      selectedRowKeys,
      type: typeof selectedRowKeys,
      isSet: selectedRowKeys instanceof Set
    });
    
    const idsArray = selectedRowKeys instanceof Set ? Array.from(selectedRowKeys) : selectedRowKeys as string[];
    
    console.log('[Confirm Items] Starting confirmation:', {
      selectedRowKeys: idsArray,
      idsArrayLength: idsArray.length,
      pendingItemsCount: pendingItems?.length || 0,
      selectedUserId,
      hasPendingItems: !!pendingItems
    });
    
    if (!selectedUserId) {
      console.error('[Confirm Items] No user selected');
      message.error('Please select a user first');
      return;
    }
    
    if (!pendingItems) {
      console.error('[Confirm Items] No pending items available');
      message.error('No pending items available');
      return;
    }
    
    if (idsArray.length === 0) {
      console.warn('[Confirm Items] No items selected');
      message.warning('No items selected');
      return;
    }

    console.log('[Confirm Items] Filtering items. Pending items:', pendingItems.map(i => ({ id: i.id, image_id: i.image_id })));
    
    const itemsToConfirm: ConfirmItemPayload[] = pendingItems
      .filter(item => {
        const matches = idsArray.includes(item.id);
        if (!matches) {
          console.debug('[Confirm Items] Item not in selection:', { itemId: item.id, selectedIds: idsArray });
        }
        return matches;
      })
      .map(item => ({
        image_id: item.image_id,
        meta_data: item.meta_data,
        similarity_info: item.similarity_info,
        force_add: false,
      }));

    console.log('[Confirm Items] Items to confirm:', itemsToConfirm.length, itemsToConfirm);

    if (itemsToConfirm.length === 0) {
      console.error('[Confirm Items] No matching items found');
      message.warning('No matching items found. Please check your selection.');
      return;
    }

    console.log('[Confirm Items] Showing confirmation modal');
    
    const proceedWithConfirmation = () => {
      console.log('[Confirm Items] Proceeding with confirmation - Calling mutation with:', itemsToConfirm);
      try {
        confirmItemsMutation.mutate(itemsToConfirm, {
          onSuccess: (data) => {
            console.log('[Confirm Items] Mutation success callback:', data);
            setSelectedPendingIds(new Set());
          },
          onError: (error: any) => {
            console.error('[Confirm Items] Mutation error:', error);
          }
        });
      } catch (error) {
        console.error('[Confirm Items] Error calling mutation:', error);
        message.error('Failed to confirm items. Please try again.');
      }
    };
    
    // Use App.useApp() modal which properly consumes context
    modal.confirm({
      title: 'Confirm Items',
      content: `Are you sure you want to add ${itemsToConfirm.length} items to the user's closet?`,
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        console.log('[Confirm Items] Modal OK clicked');
        proceedWithConfirmation();
      },
      onCancel: () => {
        console.log('[Confirm Items] Modal cancelled');
      },
    });
  };

  const handleToggleItemSelection = (itemId: string) => {
    setSelectedPendingIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const handleRemoveItem = async (item: PendingItem) => {
    Modal.confirm({
      title: 'Remove Item',
      content: 'Are you sure you want to remove this item?',
      onOk: async () => {
        try {
          setSelectedPendingIds((prev) => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
          await refetchPending();
          message.success('Item removed successfully');
        } catch (error: any) {
          message.error(`Failed to remove item: ${error.message}`);
        }
      },
    });
  };

  const pendingColumns = [
    {
      title: 'Image',
      key: 'image',
      width: 120,
      render: (item: PendingItem) => {
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const adminKey = localStorage.getItem('admin_key');
        const imageUrl = adminKey
          ? `${apiBase}/admin/images/${item.image_id}?x_admin_key=${encodeURIComponent(adminKey)}`
          : `${apiBase}/images/${item.image_id}`;
        return (
          <div style={{ 
            width: 80, 
            height: 80, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: '#ffffff',
            borderRadius: 8,
            overflow: 'hidden',
            border: '1px solid #e0e0e0'
          }}>
            <img
              src={imageUrl}
              alt="Item"
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain',
                display: 'block'
              }}
              onError={(e) => {
                e.currentTarget.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN";
              }}
            />
          </div>
        );
      },
    },
    {
      title: 'Name',
      dataIndex: ['meta_data', 'name'],
      key: 'name',
      width: 150,
      render: (text: string) => text || 'Untitled',
    },
    {
      title: 'Category',
      dataIndex: ['meta_data', 'category'],
      key: 'category',
      width: 120,
      render: (text: string) => text ? <Tag>{text}</Tag> : '-',
    },
    {
      title: 'Color',
      dataIndex: ['meta_data', 'color'],
      key: 'color',
      width: 100,
      render: (text: string) => text ? <Tag color={text.toLowerCase()}>{text}</Tag> : '-',
    },
    {
      title: 'Brand',
      dataIndex: ['meta_data', 'brand'],
      key: 'brand',
      width: 100,
    },
    {
      title: 'Style',
      dataIndex: ['meta_data', 'style'],
      key: 'style',
      width: 100,
    },
    {
      title: 'Source Index',
      dataIndex: 'source_index',
      key: 'source_index',
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (item: PendingItem) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditItem(item)}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <Space>
          <InstagramOutlined />
          <span>Instagram Batch Process</span>
        </Space>
      </Title>

      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={12}>
          <Card 
            title="Select App User" 
            size="small"
            extra={
              <Button 
                type="link" 
                size="small"
                onClick={() => setIsCreateUserModalVisible(true)}
                icon={<UserOutlined />}
              >
                Create New
              </Button>
            }
          >
            <Select
              style={{ width: '100%' }}
              placeholder="Select a user"
              showSearch
              value={selectedUserId}
              onChange={setSelectedUserId}
              filterOption={(input, option) => {
                const text = typeof option?.children === 'string' 
                  ? option.children 
                  : String(option?.children || '');
                return text.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {usersData?.users?.map((user: AdminReportsUser) => (
                <Option key={user.user_id} value={user.user_id}>
                  {user.username || user.user_id} {user.email ? `(${user.email})` : ''}
                </Option>
              ))}
            </Select>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Instagram Username" size="small">
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="Enter Instagram username"
                value={instagramUsername}
                onChange={(e) => setInstagramUsername(e.target.value)}
                prefix={<InstagramOutlined />}
                onPressEnter={() => refetchPosts()}
              />
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={() => refetchPosts()}
                loading={loadingPosts}
              >
                Load Posts
              </Button>
            </Space.Compact>
          </Card>
        </Col>
      </Row>

      {allPosts && allPosts.length > 0 && (
        <Card
          title={
            <Space>
              <span>Instagram Posts ({allPosts.length})</span>
              <Button
                size="small"
                onClick={handleSelectAllImages}
              >
                {(() => {
                  if (!allPosts || allPosts.length === 0) return 'Select All';
                  const allImageUrls: string[] = [];
                  allPosts.forEach(post => {
                    if (post.image_urls && post.image_urls.length > 0) {
                      allImageUrls.push(...post.image_urls);
                    } else if (post.image_url) {
                      allImageUrls.push(post.image_url);
                    }
                  });
                  const allSelected = allImageUrls.length > 0 && allImageUrls.every(url => selectedImages.has(url));
                  return allSelected ? 'Deselect All' : 'Select All';
                })()}
              </Button>
              <Text type="secondary">
                {selectedImages.size} image{selectedImages.size !== 1 ? 's' : ''} selected
              </Text>
            </Space>
          }
          extra={
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handleBatchProcess}
              disabled={!selectedUserId || selectedImages.size === 0 || batchProcessMutation.isPending}
              loading={batchProcessMutation.isPending}
            >
              Process Selected ({selectedImages.size} image{selectedImages.size !== 1 ? 's' : ''})
            </Button>
          }
          style={{ marginBottom: '24px' }}
        >
          <div 
            style={{ 
              display: 'flex', 
              overflowX: 'auto', 
              gap: '16px',
              paddingBottom: '8px',
              scrollbarWidth: 'thin',
              scrollbarColor: '#d0d0d0 #f0f0f0'
            }}
          >
            {allPosts.map((post: InstagramPost) => {
              // Get all images for this post (handle carousel posts)
              // Use image_urls if available (even if just one image), otherwise use image_url
              const allImages = post.image_urls && post.image_urls.length > 0
                ? post.image_urls 
                : (post.image_url ? [post.image_url] : []);
              
              if (allImages.length === 0) {
                console.warn(`Post ${post.id} has no images`);
              }
              
              // Build proxy URL for images
              const getProxyUrl = (imageUrl: string, useDirect: boolean = false) => {
                if (!imageUrl) {
                  console.warn('Empty image URL provided to getProxyUrl');
                  return '';
                }
                // If direct URL was requested (after proxy error), return original
                if (useDirect || imageErrors[imageUrl]) {
                  console.log('Using direct URL (fallback):', imageUrl.substring(0, 100));
                  return imageUrl;
                }
                const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
                // Include admin key in URL since <img> tags can't send headers
                const adminKey = localStorage.getItem('admin_key');
                const proxyUrl = adminKey 
                  ? `${apiBase}/admin/instagram/proxy-image?url=${encodeURIComponent(imageUrl)}&x_admin_key=${encodeURIComponent(adminKey)}`
                  : `${apiBase}/admin/instagram/proxy-image?url=${encodeURIComponent(imageUrl)}`;
                console.log('Using proxy URL:', proxyUrl.substring(0, 150));
                return proxyUrl;
              };
              
              const handleImageError = (imageUrl: string, element: HTMLImageElement) => {
                const currentSrc = element.src;
                console.error('Image load error:', {
                  attemptedUrl: currentSrc.substring(0, 150),
                  originalUrl: imageUrl.substring(0, 100),
                  isProxyUrl: currentSrc.includes('/admin/instagram/proxy-image'),
                  errorCount: imageErrors[imageUrl] ? 'retry' : 'first'
                });
                
                // Mark as error and try direct URL if this was a proxy attempt
                if (!imageErrors[imageUrl] && currentSrc.includes('/admin/instagram/proxy-image')) {
                  setImageErrors(prev => ({ ...prev, [imageUrl]: true }));
                  console.log('Falling back to direct URL:', imageUrl.substring(0, 100));
                  // Try direct URL as fallback
                  element.src = imageUrl;
                } else {
                  console.error('Both proxy and direct URL failed for:', imageUrl.substring(0, 100));
                }
              };
              
              return (
                <div 
                  key={post.id}
                  style={{
                    flexShrink: 0,
                    width: '300px',
                  }}
                >
                  <Card
                    hoverable
                    cover={
                      <div style={{ position: 'relative' }}>
                        {allImages.length === 1 ? (
                          // Single image
                          <div style={{ position: 'relative', paddingTop: '100%', overflow: 'hidden' }}>
                            <img
                              src={getProxyUrl(allImages[0])}
                              alt={post.caption || 'Instagram post'}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                              }}
                              onError={(e) => {
                                handleImageError(allImages[0], e.target as HTMLImageElement);
                              }}
                              loading="lazy"
                            />
                            <Checkbox
                              checked={selectedImages.has(allImages[0])}
                              onChange={() => handleSelectImage(allImages[0])}
                              style={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 10,
                              }}
                            />
                          </div>
                        ) : (
                          // Carousel - show all images with individual checkboxes
                          <div style={{ position: 'relative' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(allImages.length, 3)}, 1fr)`, gap: '2px' }}>
                              {allImages.map((imgUrl: string, idx: number) => (
                                <div key={idx} style={{ position: 'relative', paddingTop: '100%', overflow: 'hidden' }}>
                                  <img
                                    src={getProxyUrl(imgUrl)}
                                    alt={`${post.caption || 'Instagram post'} - Image ${idx + 1}`}
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      display: 'block',
                                    }}
                                    onError={(e) => {
                                      handleImageError(imgUrl, e.target as HTMLImageElement);
                                    }}
                                    loading="lazy"
                                  />
                                  <Checkbox
                                    checked={selectedImages.has(imgUrl)}
                                    onChange={() => handleSelectImage(imgUrl)}
                                    style={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      zIndex: 10,
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                            {allImages.length > 2 && (
                              <div style={{
                                position: 'absolute',
                                bottom: 8,
                                right: 8,
                                background: 'rgba(0,0,0,0.6)',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                zIndex: 10,
                              }}>
                                +{allImages.length - 2}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    }
                    size="small"
                  >
                    {post.caption && (
                      <div style={{
                        fontSize: '11px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {post.caption}
                      </div>
                    )}
                  </Card>
                </div>
              );
            })}
            {/* Lazy loading trigger */}
            {hasMorePosts && (
              <div ref={loadMoreRef} style={{ minWidth: '100px', height: '100px', flexShrink: 0 }}>
                {isLoadingMorePosts && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%' 
                  }}>
                    <Spin size="small" />
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {loadingPosts && (
        <Card>
          <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />
        </Card>
      )}

      {postsError && (
        <Card>
          <Empty 
            description={
              (postsError as any)?.response?.data?.rate_limited
                ? "Instagram rate limit reached. Please wait a few minutes before trying again."
                : (postsError as any)?.response?.data?.message || (postsError as any)?.message || "Failed to load Instagram posts. Please try again later."
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      )}

      {instagramUsername && !loadingPosts && !postsError && (!allPosts || allPosts.length === 0) && instagramData && (
        <Card>
          <Empty description="No posts found. Please check the Instagram username or integrate Instagram API." />
        </Card>
      )}

      {selectedUserId && (
        <>
          {loadingSources ? (
            <Card>
              <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />
            </Card>
          ) : effectiveSources && effectiveSources.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {/* Source Images Horizontal Carousel */}
              <Card>
                <div style={{ marginBottom: 16 }}>
                  <Space>
                    <Text strong style={{ fontSize: 16 }}>
                      Source Images
                    </Text>
                    <Tag color="blue">{effectiveSources.length} source{effectiveSources.length !== 1 ? 's' : ''}</Tag>
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={() => {
                        refetchPending();
                        refetchSources();
                      }}
                      loading={loadingPending || loadingSources}
                    >
                      Refresh
                    </Button>
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleDeleteAllSources}
                      loading={deleteSourcesMutation.isPending}
                      disabled={!effectiveSources || effectiveSources.length === 0}
                    >
                      Delete All
                    </Button>
                  </Space>
                </div>
                <div style={{ 
                  display: 'flex', 
                  gap: 16, 
                  overflowX: 'auto',
                  paddingBottom: 8,
                  scrollbarWidth: 'thin',
                  WebkitOverflowScrolling: 'touch',
                }}>
                  {effectiveSources.map((source) => {
                    const isSelected = selectedSourceIndex === source.source_index;
                    const sourceUrl = sourceImageUrls[source.source_image_id];
                    // Use pending_items_count from API if available, otherwise fall back to itemsBySource
                    const itemCount = source.pending_items_count !== undefined 
                      ? source.pending_items_count 
                      : (itemsBySource[source.source_image_id]?.length || 0);
                    const statusColor = source.status === 'ready' ? '#52c41a' : 
                                      source.status === 'error' ? '#ff4d4f' : 
                                      '#faad14';
                    
                    return (
                      <div
                        key={source.source_image_id}
                        onClick={() => setSelectedSourceIndex(source.source_index)}
                        style={{
                          flexShrink: 0,
                          width: 200,
                          cursor: 'pointer',
                          border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
                          borderRadius: 12,
                          padding: 12,
                          backgroundColor: isSelected ? '#e6f7ff' : '#fff',
                          transition: 'all 0.2s',
                        }}
                      >
                        <div style={{ position: 'relative', paddingTop: '100%', marginBottom: 8, borderRadius: 8, overflow: 'hidden', backgroundColor: '#f5f5f5' }}>
                          {sourceUrl ? (
                            <img
                              src={sourceUrl}
                              alt={`Source ${source.source_index + 1}`}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                              onError={(e) => {
                                console.error(`Failed to load source image: ${sourceUrl}`);
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#999',
                              fontSize: 12
                            }}>
                              Loading...
                            </div>
                          )}
                          <div style={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            background: statusColor,
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 'bold',
                          }}>
                            {source.status?.toUpperCase() || 'PENDING'}
                          </div>
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={(e) => handleDeleteSource(e, source.source_image_id)}
                            style={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              zIndex: 10,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: 'none',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            }}
                          />
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <Text strong style={{ fontSize: 14 }}>
                            Image {source.source_index + 1}
                          </Text>
                          <div style={{ marginTop: 4 }}>
                            <Tag color={itemCount > 0 ? 'green' : 'default'}>
                              {itemCount} item{itemCount !== 1 ? 's' : ''}
                            </Tag>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Detected Items Section */}
              {selectedSourceIndex !== null && (
                <Card
                  title={
                    <Space>
                      <span>Detected Items</span>
                      {selectedSourceIndex !== null && (
                        <Text type="secondary" style={{ fontSize: 14 }}>
                          for Image {selectedSourceIndex + 1}
                        </Text>
                      )}
                      <Tag color="blue">{currentSourceItems.length} item{currentSourceItems.length !== 1 ? 's' : ''}</Tag>
                      {loadingPending && <Spin size="small" />}
                    </Space>
                  }
                >
                  {loadingPending ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
                      <Spin size="large" />
                    </div>
                  ) : currentSourceItems.length > 0 ? (
                    <>
                      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
                        <Space>
                          <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const logData = { 
                                selectedPendingIds: Array.from(selectedPendingIds),
                                size: selectedPendingIds.size,
                                disabled: selectedPendingIds.size === 0,
                                isPending: confirmItemsMutation.isPending,
                                selectedUserId,
                                hasPendingItems: !!pendingItems
                              };
                              console.log('[Button] Confirm button clicked', logData);
                              // Alert for immediate debugging feedback
                              alert(`Button clicked! Selected: ${selectedPendingIds.size} items. User: ${selectedUserId || 'none'}. Check console.`);
                              
                              if (selectedPendingIds.size === 0) {
                                console.warn('[Button] Button clicked but disabled - no items selected');
                                message.warning('Please select items first');
                                return;
                              }
                              handleConfirmItems(selectedPendingIds);
                            }}
                            disabled={selectedPendingIds.size === 0 || confirmItemsMutation.isPending}
                            loading={confirmItemsMutation.isPending}
                          >
                            Confirm Selected ({selectedPendingIds.size})
                          </Button>
                          <Button
                            onClick={() => setSelectedPendingIds(new Set())}
                            disabled={selectedPendingIds.size === 0}
                          >
                            Clear Selection
                          </Button>
                        </Space>
                      </div>
                      <Row gutter={[16, 16]}>
                        {currentSourceItems.map((item) => (
                          <Col 
                            key={item.id}
                            xs={24}      // 1 column on extra small screens
                            sm={12}      // 2 columns on small screens
                            md={8}       // 3 columns on medium screens
                            lg={6}       // 4 columns on large screens
                            xl={6}       // 4 columns on extra large screens
                            xxl={4}      // 6 columns on 2xl screens
                          >
                            <DetectedItemCard
                              item={item}
                              selected={selectedPendingIds.has(item.id)}
                              onToggleSelect={handleToggleItemSelection}
                              onEdit={handleEditItem}
                              onRemove={handleRemoveItem}
                              onSimilarityClick={(item) => {
                                message.info('Similar items feature - to be implemented');
                              }}
                            />
                          </Col>
                        ))}
                      </Row>
                    </>
                  ) : (
                    <Empty 
                      description={
                        effectiveSources.find(s => s.source_index === selectedSourceIndex)?.status === 'pending' 
                          ? "Processing... Items will appear here when ready."
                          : "No items extracted from this source yet."
                      }
                    />
                  )}
                </Card>
              )}
            </div>
          ) : (
            <Card>
              <Empty description="No pending items. Process some images first." />
            </Card>
          )}
        </>
      )}

      <EditTagsModal
        visible={isEditingModalVisible}
        item={editingItem}
        onSave={(itemId, metaData) => {
          if (selectedUserId) {
            updateItemMutation.mutate(
              { userId: selectedUserId, itemId, data: { meta_data: metaData } },
              {
                onSuccess: () => {
                  setIsEditingModalVisible(false);
                  setEditingItem(null);
                  refetchPending();
                },
              }
            );
          }
        }}
        onCancel={() => {
          setIsEditingModalVisible(false);
          setEditingItem(null);
        }}
        loading={updateItemMutation.isPending}
      />

      {/* Legacy Edit Modal - keeping for backward compatibility */}
      <Modal
        title="Edit Item"
        open={false}
        onOk={handleSaveEdit}
        onCancel={() => {
          setIsEditingModalVisible(false);
          setEditingItem(null);
          editingForm.resetFields();
        }}
        confirmLoading={updateItemMutation.isPending}
        width={600}
      >
        <Form form={editingForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Name">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="category" label="Category">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="color" label="Color">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="size" label="Size">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="brand" label="Brand">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="material" label="Material">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="season" label="Season">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="style" label="Style">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="pattern" label="Pattern">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="occasion" label="Occasion">
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label="Notes">
                <TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Create User Modal */}
      <Modal
        title="Create New User"
        open={isCreateUserModalVisible}
        onCancel={() => {
          setIsCreateUserModalVisible(false);
          createUserForm.resetFields();
        }}
        onOk={() => {
          createUserForm.submit();
        }}
        confirmLoading={createUserMutation.isPending}
        okText="Create"
        cancelText="Cancel"
      >
        <Form
          form={createUserForm}
          layout="vertical"
          onFinish={(values) => {
            createUserMutation.mutate({
              username: values.username,
              password: values.password,
              email: values.email || undefined,
              full_name: values.full_name || undefined,
              telegram_id: values.telegram_id || undefined,
            });
          }}
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: 'Username is required' },
              { min: 3, message: 'Username must be at least 3 characters' },
            ]}
          >
            <Input placeholder="Enter username" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Password is required' },
              { min: 6, message: 'Password must be at least 6 characters' },
            ]}
          >
            <Input.Password placeholder="Enter password" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="Enter email (optional)" />
          </Form.Item>

          <Form.Item
            name="full_name"
            label="Full Name"
          >
            <Input placeholder="Enter full name (optional)" />
          </Form.Item>

          <Form.Item
            name="telegram_id"
            label="Telegram ID"
          >
            <Input placeholder="Enter Telegram ID (optional)" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InstagramBatchProcess;
